/**
 * Where the refresh token lives between reloads, and nothing else. See D6.
 *
 * The access token is never written here: it stays in memory inside the
 * session, so a script that reads storage gets a token that is only worth
 * something by being spent, and spending it rotates it out from under the
 * signed-in tab, which then signs out.
 *
 * Every access is wrapped: storage throws in some private windows and when
 * site data is blocked. A store that cannot be read behaves as empty, and a
 * write that fails leaves the session alive until the next reload.
 */

export const REFRESH_KEY = 'veriplus.portal.refreshToken';

export interface RefreshStore {
  read(): string | null;
  write(token: string): void;
  clear(): void;
}

export function createRefreshStore(
  storage: () => Storage = () => window.localStorage,
): RefreshStore {
  return {
    read() {
      try {
        const value = storage().getItem(REFRESH_KEY);
        return value === null || value === '' ? null : value;
      } catch {
        return null;
      }
    },
    write(token) {
      try {
        storage().setItem(REFRESH_KEY, token);
      } catch {
        // Unwritable storage costs the reload, not the session.
      }
    },
    clear() {
      try {
        storage().removeItem(REFRESH_KEY);
      } catch {
        // Nothing to clear if it cannot be reached.
      }
    },
  };
}
